import { SelectOptionInterface } from '@/components/atoms/MultiSelect';
import { SingleValue } from 'react-select/dist/declarations/src/types';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { computeText } from '@/locales/utils';
import { useSelector } from 'react-redux';
import { getUseSalesSummaryExportWithTaxExcluded } from '@/redux/launchDarkly';

export enum ReportType {
    SALES_SUMMARY = 'sales_summary',
    COLLECTIVE_CATERING_SALES_SUMMARY = 'collective_catering_sales_summary',
    COLLECTIVE_CATERING_SALES_SUMMARY_EXCLUDE_TAX = 'collective_catering_sales_summary_exclude_tax',
}

export enum AccountedOrdersFilter {
    ALL = 'ALL',
    ORDERS_ONLY = 'ORDERS_ONLY',
    CONTRA_ENTRIES_ONLY = 'CONTRA_ENTRIES_ONLY',
}

type ReportTypeSelectProps = {
    reportTypeOptions: SelectOptionInterface<ReportType>[];
    selectedReportType: SelectOptionInterface<ReportType>;
    handleSelectReportTypeChange: (option: SingleValue<SelectOptionInterface<ReportType>>) => void;
};

export const useReportTypeSelectProps = (): ReportTypeSelectProps => {
    const intl = useIntl();
    const collectiveCateringSalesSummaryReportTypeOption = {
        value: ReportType.COLLECTIVE_CATERING_SALES_SUMMARY,
        label: computeText(intl, 'salesSummary.form.reportType.collectiveCateringSalesSummary'),
    };
    const collectiveCateringSalesSummaryExclTaxReportTypeOption = {
        value: ReportType.COLLECTIVE_CATERING_SALES_SUMMARY_EXCLUDE_TAX,
        label: computeText(intl, 'salesSummary.form.reportType.collectiveCateringSalesSummaryExclTax'),
    };
    const salesSummaryReportTypeOption = {
        value: ReportType.SALES_SUMMARY,
        label: computeText(intl, 'salesSummary.form.reportType.salesSummary'),
    };
    const displaySalesSummaryExportWithTaxExcludedOption = useSelector(getUseSalesSummaryExportWithTaxExcluded);

    const reportTypeOptions = [collectiveCateringSalesSummaryReportTypeOption, salesSummaryReportTypeOption];
    if (displaySalesSummaryExportWithTaxExcludedOption)
        reportTypeOptions.push(collectiveCateringSalesSummaryExclTaxReportTypeOption);

    const [selectedReportType, setSelectedReportType] = useState<SelectOptionInterface<ReportType>>(
        collectiveCateringSalesSummaryReportTypeOption,
    );

    const handleSelectReportTypeChange = (option: SingleValue<SelectOptionInterface<ReportType>>) => {
        setSelectedReportType(option || collectiveCateringSalesSummaryReportTypeOption);
    };

    return {
        reportTypeOptions,
        selectedReportType,
        handleSelectReportTypeChange,
    };
};

type AccountedOrdersSelectProps = {
    accountedOrdersOptions: SelectOptionInterface<AccountedOrdersFilter>[];
    selectedAccountedOrders: SelectOptionInterface<AccountedOrdersFilter>;
    handleSelectAccountedOrdersChange: (option: SingleValue<SelectOptionInterface<AccountedOrdersFilter>>) => void;
};

export const useAccountedOrdersSelectProps = (): AccountedOrdersSelectProps => {
    const intl = useIntl();
    const allOption = {
        value: AccountedOrdersFilter.ALL,
        label: computeText(intl, 'salesSummary.form.accountedOrders.all'),
    };
    const ordersOnlyOption = {
        value: AccountedOrdersFilter.ORDERS_ONLY,
        label: computeText(intl, 'salesSummary.form.accountedOrders.ordersOnly'),
    };
    const contraEntriesOnlyOption = {
        value: AccountedOrdersFilter.CONTRA_ENTRIES_ONLY,
        label: computeText(intl, 'salesSummary.form.accountedOrders.contraEntriesOnly'),
    };

    const accountedOrdersOptions = [allOption, ordersOnlyOption, contraEntriesOnlyOption];

    const [selectedAccountedOrders, setSelectedAccountedOrders] =
        useState<SelectOptionInterface<AccountedOrdersFilter>>(allOption);

    const handleSelectAccountedOrdersChange = (option: SingleValue<SelectOptionInterface<AccountedOrdersFilter>>) => {
        setSelectedAccountedOrders(option || allOption);
    };

    return {
        accountedOrdersOptions,
        selectedAccountedOrders,
        handleSelectAccountedOrdersChange,
    };
};
